<!--
  Everything we know about the lead of a lead bot, for moinAI admins: opened
  from the review banner and the list of demo requests, with the review they
  already loaded. The bot's name can be changed here, like in "Edit Bot" on the
  tools page.
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Save } from '@lucide/vue';
import BotService from '@/services/bot';
import type { DemoLeadReview, DemoLeadStatus } from '@/services/demo';
import { useBotsStore } from '@/stores/bots';
import { useHubUiStore } from '@/stores/hubUi';
import HubDialog from '@/components/hub/HubDialog.vue';
import { Input } from '@/components/ui/input';
import { formatDateTimeShortMonth } from '@/utils/formatDateTime';

const props = defineProps<{
  modelValue: boolean;
  lead: DemoLeadReview;
}>();

const emit = defineEmits<{
  'update:modelValue': [open: boolean];
  /** The bot got this new name */
  renamed: [name: string];
}>();

const { t, te } = useI18n();
const botsStore = useBotsStore();
const hubUiStore = useHubUiStore();

const botName = ref('');
// From the review, whenever the dialog opens or the name changed
watch(() => [props.modelValue, props.lead.bot?.name], () => {
  botName.value = props.lead.bot?.name ?? '';
}, { immediate: true });

/** Renaming goes through the bot's own route, which needs the admin to have the bot */
const canRename = computed(() => {
  const botId = props.lead.bot?.uniqueBotId;
  return !!botId && (botsStore.botsGetter || []).some((bot: { uniqueBotId: string }) => bot.uniqueBotId === botId);
});
// Trimmed already, by `v-model.trim`
const nameChanged = computed(() => !!botName.value && botName.value !== (props.lead.bot?.name ?? ''));
const saving = ref(false);

async function saveName() {
  const botId = props.lead.bot?.uniqueBotId;
  if (!botId || !canRename.value || !nameChanged.value || saving.value) return;

  saving.value = true;
  const name = botName.value;
  const { success, error } = await BotService.updateBotName(botId, name);
  saving.value = false;

  hubUiStore.addAlert({
    message: success ? 'Bot-Name gespeichert.' : error?.message || 'Der Bot-Name konnte nicht gespeichert werden.',
    type: success ? 'success' : 'error',
  });
  if (!success) return;

  emit('renamed', name);
  // So the app bar and the bot picker show the new name too
  botsStore.get();
}

const statusLabels: Record<DemoLeadStatus, string> = {
  unconfirmed: 'E-Mail unbestätigt',
  submitted: 'Eingereicht',
  building: 'Wird aufgebaut',
  review: 'In Prüfung',
  released: 'Freigegeben',
  redeemed: 'Einladung angenommen',
};

const formatDate = (date: string | null) => formatDateTimeShortMonth(date) || null;
const yesNo = (value: boolean) => (value ? 'Ja' : 'Nein');

interface Row {
  label: string;
  value: string | null;
  /** Rendered as a link */
  href?: string;
}

const sections = computed<{ title: string; rows: Row[] }[]>(() => {
  const { lead } = props;
  const language = lead.contact.language;

  return [
    {
      title: 'Kontakt',
      rows: [
        { label: 'Name', value: [lead.contact.firstName, lead.contact.lastName].filter(Boolean).join(' ') || null },
        { label: 'E-Mail', value: lead.contact.email, href: lead.contact.email ? `mailto:${lead.contact.email}` : undefined },
        { label: 'Sprache', value: language && te(`languages.${language}`) ? t(`languages.${language}`) : language },
      ],
    },
    {
      title: 'Unternehmen',
      rows: [
        { label: 'Firma (laut Website)', value: lead.companyName },
        { label: 'Website', value: lead.website.url, href: lead.website.url ?? undefined },
        { label: 'Seitentitel', value: lead.website.name },
        { label: 'Ziel', value: lead.purpose ? t(`demoV3.goals.${lead.purpose}.title`) : null },
      ],
    },
    {
      title: 'Einwilligungen',
      rows: [
        { label: 'Datenschutz & AGB', value: lead.consent ? yesNo(lead.consent.legal) : null },
        { label: 'Marketing', value: lead.consent ? yesNo(lead.consent.marketing) : null },
        { label: 'Erteilt am', value: formatDate(lead.consent?.at ?? null) },
        { label: 'Textversion', value: lead.consent?.version ?? null },
      ],
    },
    {
      title: 'Query Parameters',
      // Missing from APIs older than the hub
      rows: lead.queryParams?.length
        ? lead.queryParams.map(({ key, value }) => ({ label: key, value }))
        : [{ label: 'Parameter', value: null }],
    },
    {
      title: 'Verlauf',
      rows: [
        { label: 'Status', value: statusLabels[lead.leadStatus] ?? lead.leadStatus },
        { label: 'Eingereicht', value: formatDate(lead.submittedAt) },
        { label: 'Automatische Freigabe', value: formatDate(lead.readyAt) },
        { label: 'Zurückgehalten', value: lead.isPaused ? `von ${lead.heldBy ?? 'einem Admin'}, am ${formatDate(lead.heldAt) ?? '–'}` : null },
        {
          label: 'Freigegeben',
          value: lead.releasedAt
            ? `${formatDate(lead.releasedAt)}, ${lead.releasedBy === 'auto' ? 'automatisch' : `von ${lead.releasedBy}`}`
            : null,
        },
        { label: 'Eingeladen', value: formatDate(lead.invitedAt) },
        { label: 'Ergebnis der Einladung', value: lead.inviteResult },
        { label: 'Einladung angenommen', value: formatDate(lead.redeemedAt) },
      ],
    },
  ];
});
</script>

<template>
  <HubDialog
    :model-value="modelValue"
    title="Lead-Details"
    :hide-primary="!canRename"
    primary-label="Speichern"
    :primary-icon="Save"
    :disabled="!nameChanged || saving"
    :loading="saving"
    manual-primary
    secondary-label="Schließen"
    max-width="560px"
    @update:model-value="$emit('update:modelValue', $event)"
    @on-primary="saveName"
  >
    <div data-cy="lead-details" class="grid gap-5">
      <section data-cy="lead-details-bot">
        <h3 class="mb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">Bot</h3>
        <dl class="grid grid-cols-[11rem_1fr] items-center gap-x-4 gap-y-1 text-sm max-sm:grid-cols-1">
          <dt class="text-muted-foreground">Bot-ID</dt>
          <dd class="font-mono wrap-anywhere">{{ lead.bot?.uniqueBotId ?? '–' }}</dd>
          <dt class="text-muted-foreground">Staging-Bot-ID</dt>
          <dd class="font-mono wrap-anywhere">{{ lead.bot?.stagingBotId ?? '–' }}</dd>
          <dt class="text-muted-foreground">
            <label for="lead-details-bot-name">Bot-Name</label>
          </dt>
          <dd>
            <template v-if="lead.bot">
              <Input
                id="lead-details-bot-name"
                v-model.trim="botName"
                data-cy="lead-details-bot-name"
                class="h-8"
                :disabled="!canRename || saving"
                @keydown.enter="saveName"
              />
              <p v-if="!canRename" class="mt-1 text-xs text-muted-foreground">
                Füge dich zum Bot hinzu, um ihn umzubenennen.
              </p>
            </template>
            <template v-else>–</template>
          </dd>
        </dl>
      </section>

      <section v-for="section in sections" :key="section.title">
        <h3 class="mb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">{{ section.title }}</h3>
        <!-- Fixed label width, so the values line up across the sections -->
        <dl class="grid grid-cols-[11rem_1fr] gap-x-4 gap-y-1 text-sm max-sm:grid-cols-1">
          <template v-for="row in section.rows" :key="row.label">
            <dt class="text-muted-foreground">{{ row.label }}</dt>
            <dd class="wrap-anywhere">
              <a v-if="row.href && row.value" :href="row.href" target="_blank" rel="noopener" class="underline underline-offset-2">{{ row.value }}</a>
              <template v-else>{{ row.value ?? '–' }}</template>
            </dd>
          </template>
        </dl>
      </section>
    </div>
  </HubDialog>
</template>
